import { useNavigate, useSearch } from "@tanstack/react-router";
import { Button } from "primereact/button";
import { type FC, useEffect } from "react";
import { useForm } from "react-hook-form";
import AuthCard, { submitButtonPt } from "@/components/AuthCard";
import authStyles from "@/components/AuthCard.module.css";
import BackToLoginLink from "@/components/BackToLoginLink";
import { ROUTES } from "@/constants/routes";
import { TEXT } from "@/constants/text";
import PasswordInputField from "@/pages/auth/NewPasswordPage/PasswordInputField";
import PasswordRequirementsList from "@/pages/auth/NewPasswordPage/PasswordRequirementsList";
import styles from "@/pages/auth/NewPasswordPage/NewPasswordForm.module.css";
import { isPasswordValid } from "@/pages/auth/NewPasswordPage/passwordRequirements";
import { setPassword } from "@/apis/clients/sessionClient";

export interface NewPasswordFormValues {
  confirmPassword: string;
  password: string;
}

const NewPasswordForm: FC = () => {
  const navigate = useNavigate();
  // The route id, not the path: this route is nested under a pathless layout
  // whose id namespaces it, so the two differ. A rename breaks this at runtime
  // rather than at compile time, which is why the tests below drive the real
  // router rather than rendering the component directly.
  const { token } = useSearch({ from: "/auth-layout/reset-password/new" });
  const {
    clearErrors,
    control,
    getValues,
    handleSubmit,
    setError,
    trigger,
    watch,
    formState: { errors, isSubmitted, isSubmitting, touchedFields },
  } = useForm<NewPasswordFormValues>({ mode: "onBlur" });
  const passwordValue = watch("password");
  const confirmPasswordValue = watch("confirmPassword");
  const passwordsMatch =
    Boolean(confirmPasswordValue) && confirmPasswordValue === passwordValue;
  const isPasswordValidated = Boolean(touchedFields.password) || isSubmitted;
  const isPasswordValueValid = isPasswordValid(passwordValue ?? "");
  // RHF only sets errors.password on blur, so the initial error is delayed until
  // the field loses focus. After that, the live !isPasswordValueValid check clears
  // it the moment a valid value is typed without needing another blur.
  const showPasswordError = Boolean(errors.password) && !isPasswordValueValid;
  // Gate confirm errors until after the first blur or a submit attempt — errors
  // shown before the user has touched the field are premature.
  const confirmTouched = Boolean(touchedFields.confirmPassword) || isSubmitted;
  // Suppress confirm errors when the values already match (stale RHF state)
  // and don't show them at all while New Password is empty — confirm's state
  // is only meaningful when there is something to compare against.
  const showConfirmError =
    Boolean(errors.confirmPassword) &&
    confirmTouched &&
    !passwordsMatch &&
    Boolean(passwordValue);

  // The token is the whole credential, so submitting has to spend it. An
  // earlier version validated the password and navigated straight to login
  // without ever calling the backend, which left every invited account stuck
  // at `invited` and unable to sign in at all.
  const onSubmit = async (values: NewPasswordFormValues) => {
    clearErrors("root");

    let rejection: string | null;
    try {
      rejection = await setPassword(token, values.password);
    } catch {
      setError("root", { message: TEXT.auth.newPassword.setPasswordFailed });
      return;
    }

    if (rejection === null) {
      // No session is created by set-password, by design: signing in with the
      // new password proves it works while the user is still here.
      navigate({ to: ROUTES.LOGIN });
      return;
    }
    setError("root", {
      message: rejection || TEXT.auth.newPassword.linkInvalid,
    });
  };

  // Re-validate confirm whenever the password field changes so a previously
  // matching pair that becomes mismatched shows an error immediately.
  // Skip when password is empty — that would set a stale required error on confirm.
  useEffect(() => {
    if (confirmTouched && passwordValue) {
      trigger("confirmPassword");
    }
  }, [passwordValue, confirmTouched, trigger]);

  // A link with no token cannot succeed, so the form is not offered. Choosing
  // and confirming a password only to be told the link was broken is worse
  // than being told immediately.
  if (!token) {
    return (
      <div>
        <AuthCard
          beforeTitle={
            <BackToLoginLink label={TEXT.auth.newPassword.backToLoginLink} />
          }
          subtitle={TEXT.auth.newPassword.linkInvalid}
          title={TEXT.auth.newPassword.linkInvalidTitle}
        >
          <BackToLoginLink label={TEXT.auth.newPassword.backToLoginLink} />
        </AuthCard>
      </div>
    );
  }

  return (
    <div>
      <AuthCard
        beforeTitle={
          <BackToLoginLink label={TEXT.auth.newPassword.backToLoginLink} />
        }
        subtitle={TEXT.auth.newPassword.subtitle}
        title={TEXT.auth.newPassword.title}
      >
        <form
          className={`${authStyles.form} p-fluid`}
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className={authStyles.field}>
            <label
              className={`${authStyles.label} ${
                showPasswordError ? styles.labelError : ""
              }`}
              htmlFor="password"
            >
              {TEXT.auth.newPassword.newPasswordLabel}
            </label>
            <PasswordInputField
              control={control}
              hasError={showPasswordError}
              isSuccess={isPasswordValueValid}
              name="password"
              rules={{
                deps: ["confirmPassword"],
                required: TEXT.auth.newPassword.newPasswordRequired,
                validate: (value) => isPasswordValid(value),
              }}
            />
            {errors.password?.type === "required" && !passwordValue && (
              <span className={styles.errorMessage} role="alert">
                <i aria-hidden="true" className="pi pi-times" />
                {TEXT.auth.newPassword.newPasswordRequired}
              </span>
            )}
          </div>
          <div className={authStyles.field}>
            <label
              className={`${authStyles.label} ${
                showConfirmError ? styles.labelError : ""
              }`}
              htmlFor="confirmPassword"
            >
              {TEXT.auth.newPassword.confirmPasswordLabel}
            </label>
            <PasswordInputField
              control={control}
              hasError={showConfirmError}
              isSuccess={passwordsMatch}
              name="confirmPassword"
              rules={{
                required: TEXT.auth.newPassword.confirmPasswordRequired,
                validate: (value) =>
                  value === getValues("password") ||
                  TEXT.auth.newPassword.passwordMismatch,
              }}
            />
            {showConfirmError && (
              <span className={styles.errorMessage} role="alert">
                <i aria-hidden="true" className="pi pi-times" />
                {errors.confirmPassword?.message}
              </span>
            )}
            {passwordsMatch && (
              <span className={styles.matchMessage}>
                <i aria-hidden="true" className="pi pi-check" />
                {TEXT.auth.newPassword.passwordsMatch}
              </span>
            )}
          </div>
          <PasswordRequirementsList
            isValidated={isPasswordValidated}
            password={passwordValue ?? ""}
          />
          {errors.root && (
            <span className={styles.errorMessage} role="alert">
              <i aria-hidden="true" className="pi pi-times" />
              {errors.root.message}
            </span>
          )}
          <Button
            disabled={!isPasswordValueValid || !passwordsMatch || isSubmitting}
            icon="pi pi-arrow-right"
            iconPos="right"
            label={TEXT.auth.newPassword.submitButton}
            pt={submitButtonPt}
            type="submit"
          />
        </form>
      </AuthCard>
    </div>
  );
};

export default NewPasswordForm;
